import { TypedEventEmitter } from './event-emitter';
const BIGINT_FIELDS = /("(?:id|session_id|sender|handle_id)")\s*:\s*(\d{16,})/g;
function safeJsonParse(text) {
    return JSON.parse(text.replace(BIGINT_FIELDS, '$1:"$2"'));
}
function generateTransaction() {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < 12; i++) {
        result += chars[Math.floor(Math.random() * chars.length)];
    }
    return result;
}
export class JanusTransport extends TypedEventEmitter {
    constructor(server, options = {}) {
        super({ debug: options.debug });
        this.sessionId = null;
        this.polling = false;
        this.eventSource = null;
        this.sseFailures = 0;
        this.pending = new Map();
        this.eventBuffer = new Map();
        this.destroyed = false;
        this.server = server.replace(/\/$/, '');
        this.maxSSEFailures = options.maxSSEFailures ?? 10;
        this.getHeaders = options.headers;
        this.debug = options.debug ?? false;
    }
    get currentSessionId() {
        return this.sessionId;
    }
    setSessionId(id) {
        this.sessionId = id;
    }
    log(...args) {
        if (this.debug)
            console.log('[JanusTransport]', ...args);
    }
    async request(path, body, signal) {
        signal?.throwIfAborted();
        const url = `${this.server}${path}`;
        this.log('POST', path, body.janus, body.body);
        const response = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', ...this.getHeaders?.() },
            body: JSON.stringify(body),
            signal,
        });
        if (!response.ok)
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        const parsed = safeJsonParse(await response.text());
        this.log('Response:', parsed.janus, parsed.transaction);
        return parsed;
    }
    async sendWithTransaction(path, body, options = {}) {
        const { waitForEvent = false, timeoutMs = 30000, signal } = options;
        signal?.throwIfAborted();
        const transaction = generateTransaction();
        const postResponse = await this.request(path, { ...body, transaction }, signal);
        if (postResponse.janus === 'error' || !waitForEvent || postResponse.janus !== 'ack') {
            return postResponse;
        }
        return this.waitForTransaction(transaction, timeoutMs, signal);
    }
    waitForTransaction(transaction, timeoutMs, signal) {
        const buffered = this.eventBuffer.get(transaction);
        if (buffered) {
            clearTimeout(buffered.timer);
            this.eventBuffer.delete(transaction);
            return Promise.resolve(buffered.response);
        }
        return new Promise((resolve, reject) => {
            const cleanup = () => {
                clearTimeout(timer);
                signal?.removeEventListener('abort', onAbort);
                this.pending.delete(transaction);
            };
            const timer = setTimeout(() => {
                cleanup();
                reject(new Error(`Transaction ${transaction} timed out after ${timeoutMs}ms`));
            }, timeoutMs);
            const onAbort = () => {
                cleanup();
                reject(signal?.reason ?? new DOMException('Aborted', 'AbortError'));
            };
            signal?.addEventListener('abort', onAbort, { once: true });
            this.pending.set(transaction, {
                resolve: (response) => { cleanup(); resolve(response); },
                reject: (error) => { cleanup(); reject(error); },
                timer,
            });
        });
    }
    startPolling() {
        if (this.polling || !this.sessionId)
            return Promise.resolve();
        this.polling = true;
        this.destroyed = false;
        this.sseFailures = 0;
        return this.connectSSE();
    }
    stopPolling() {
        this.polling = false;
        if (this.eventSource) {
            this.eventSource.close();
            this.eventSource = null;
        }
    }
    connectSSE() {
        if (!this.polling || this.destroyed || !this.sessionId)
            return Promise.resolve();
        if (typeof EventSource === 'undefined')
            return Promise.resolve();
        const headers = this.getHeaders?.() ?? {};
        const token = headers.Token || headers.token || '';
        const url = `${this.server}/sse/${this.sessionId}?token=${encodeURIComponent(token)}`;
        this.log('SSE connecting:', url);
        return new Promise((resolve) => {
            const es = new EventSource(url);
            this.eventSource = es;
            es.onopen = () => {
                this.log('SSE connected');
                this.sseFailures = 0;
                resolve();
            };
            es.onmessage = (msg) => {
                try {
                    const event = safeJsonParse(msg.data);
                    this.handlePollEvent(event);
                }
                catch (err) {
                    this.log('SSE parse error:', err);
                }
            };
            es.onerror = () => {
                if (!this.polling || this.destroyed)
                    return;
                this.sseFailures++;
                if (es.readyState === 2) {
                    this.log(`SSE permanent failure (${this.sseFailures}/${this.maxSSEFailures})`);
                    es.close();
                    this.eventSource = null;
                    resolve();
                    if (this.sseFailures >= this.maxSSEFailures) {
                        this.emit('error', new Error(`SSE failed ${this.sseFailures} times`));
                        return;
                    }
                    const delay = Math.min(1000 * Math.pow(1.5, this.sseFailures - 1), 15000);
                    setTimeout(() => this.connectSSE(), delay);
                    return;
                }
                this.log(`SSE transient error (${this.sseFailures}/${this.maxSSEFailures}), browser will retry`);
                if (this.sseFailures >= this.maxSSEFailures) {
                    es.close();
                    this.eventSource = null;
                    this.emit('error', new Error(`SSE failed ${this.sseFailures} times`));
                }
            };
        });
    }
    handlePollEvent(event) {
        const tx = 'transaction' in event ? event.transaction : undefined;
        if (event.janus === 'keepalive')
            return;
        if (event.janus === 'timeout') {
            this.emit('timeout', undefined);
            return;
        }
        if (tx && this.pending.has(tx)) {
            this.pending.get(tx).resolve(event);
            return;
        }
        if (tx) {
            const timer = setTimeout(() => {
                this.eventBuffer.delete(tx);
                this.emit('event', event);
            }, 5000);
            this.eventBuffer.set(tx, { response: event, timer });
            return;
        }
        this.emit('event', event);
    }
    destroy() {
        this.destroyed = true;
        this.stopPolling();
        for (const [, p] of this.pending) {
            clearTimeout(p.timer);
            p.reject(new Error('Transport destroyed'));
        }
        this.pending.clear();
        for (const [, b] of this.eventBuffer)
            clearTimeout(b.timer);
        this.eventBuffer.clear();
        this.removeAllListeners();
    }
}
export { generateTransaction, safeJsonParse };
